import { ReactNode, useEffect, useMemo } from 'react';
import loadable from '@loadable/component';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import { COMPANY_VALUATION_MULTIPLE_VS_INDUSTRY } from '@/constants/feedbackSections';
import { Button } from '@simplywallst/ui-core';
import { useIndustryContext } from './components/IndustryContextProvider';
import ReportSubSection from '@components/ReportSubSection';
import MultipleDropdown from '../MultipleDropdown';
import { DropdownWrapper, IndustryChartFallback } from './styled';
import { getMultipleFullName } from '../../utils';
import type { PreferredMultiple } from '../../selector';
import { useSelector, shallowEqual } from 'react-redux';
import selector from '../../selector';
import { useVisibleSectionsContext } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import ScreenerLinkButton from '@/pages/CompanyReport/components/ScreenerLinkButton';
import { GridViewOptions } from '@/pages/CompanyReport/constants/constants';
import { VALUATION_RATIO_VS_INDUSTRY } from '@/constants/anchors';

const MultipleVsIndustryChartContainer = loadable(
  () =>
    import(
      './components/MultipleVsIndustryChart/MultipleVsIndustryChartContainer'
    ),
  {
    ssr: false,
    fallback: <IndustryChartFallback />,
  }
);

const MultipleVsIndustryTable = loadable(
  () => import('./components/MultipleVsIndustryTable')
);

interface MultipleVsIndustryProps {
  preferredMultiple: PreferredMultiple;
  statements?: ReactNode;
  getVsIndustryDescription: (multiple: string) => string;
}

const MultipleVsIndustry = ({
  preferredMultiple,
  statements,
  getVsIndustryDescription,
}: MultipleVsIndustryProps) => {
  const { multiple, setMultiple } = useIndustryContext();
  //Get info about selected company from API
  const { multiples, tickerSymbol, uniqueSymbol, canonicalUrl } = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);
  const { isPristine } = useVisibleSectionsContext();

  //Initially set multiple selection to the company's preferred one
  useEffect(
    () => setMultiple(preferredMultiple),
    [preferredMultiple, setMultiple]
  );

  const helpContent = useMemo(
    () => (
      <div>
        <p>
          Comparing a company to its industry is useful to gain a wider
          perspective of how it is valued. The advantage is that you generally
          have more companies to compare to, with the trade off being that you
          are comparing with companies that may be at a different stage of the
          lifecycle to the company you are valuing.
        </p>
        <p>
          For a more detailed breakdown of how compare stocks to their industry,
          please check out our{' '}
          <Button
            as="a"
            target="_blank"
            href={
              '/help/articles/hc/en-us/articles/4751563581071-Understanding-the-Valuation-section-in-the-company-report#Comparison-to-Industry'
            }
            rel="noopener noreferrer"
            styleType="strongLink"
          >
            Help Centre.
          </Button>
        </p>
      </div>
    ),
    []
  );

  return (
    <>
      <ReportSubSection
        titleElement={isPristine ? 'h2' : 'h3'}
        title={`${getMultipleFullName(multiple)} Ratio vs Industry`}
        description={getVsIndustryDescription(multiple)}
        bodyRender={() => (
          <>
            <DropdownWrapper>
              <MultipleDropdown
                id="vsIndustry"
                multiple={multiple}
                preferredMultiple={preferredMultiple}
                onSelect={setMultiple}
              />
            </DropdownWrapper>
            <ChartWrapper
              id="valuation-multiple-vs-industry"
              chart={
                <>
                  <MultipleVsIndustryChartContainer
                    multiples={multiples}
                    tickerSymbol={tickerSymbol}
                    uniqueSymbol={uniqueSymbol}
                    canonicalUrl={canonicalUrl}
                  />
                  <MultipleVsIndustryTable />
                </>
              }
              table={
                <>
                  <IndustryChartFallback />
                  <MultipleVsIndustryTable />
                </>
              }
              feedbackLabel={COMPANY_VALUATION_MULTIPLE_VS_INDUSTRY}
              help={helpContent}
              helpTitle="Preferred Ratio vs Industry"
              preRenderHeight={614}
              preRenderHeightSmDown={628}
              hideDataButton
            />
          </>
        )}
        statements={statements}
      />
      <ScreenerLinkButton
        ideaName="strong-past-earnings-growth-p-e-ratio-below-20"
        gridViewOptionId={GridViewOptions.GRID_VIEW_IN_CONTEXT_VALUATION}
        subSection={VALUATION_RATIO_VS_INDUSTRY}
      />
    </>
  );
};
export default MultipleVsIndustry;
